import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Loader2,
  TrendingUp,
  ShoppingCart,
  ListChecks,
  LayoutDashboard,
  Brain,
  Plug,
  BellRing,
  Bot,
  QrCode,
  BarChart3,
  Shield,
  Languages,
  CreditCard,
  Check,
  Sparkles,
  MessageCircleQuestion,
  Clock,
  Phone
} from "lucide-react";
import logoUrl from "@/assets/waiterix-logo.png";
import { AnimatedGauge } from "@/components/AnimatedGauge";
import { Link, useLocation } from "wouter";
import { signInWithGoogle, signInWithEmail, signUpWithEmail } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Pricing } from "@/components/pricing";
import { MarqueeCarousel } from "@/components/ui/marquee-carousel";


// --- UPDATED featureCards with shorter descriptions from correction ---
const featureCards = [
  {
    icon: Bot,
    title: "AI Waiter Assistant",
    description: "Answers questions, recommends dishes, and takes orders.",
    color: "from-primary to-orange-400"
  },
  {
    icon: QrCode,
    title: "QR Table Ordering",
    description: "Guests browse, order, and re-order from their phones.",
    color: "from-orange-500 to-primary"
  },
  {
    icon: ListChecks,
    title: "Order Tracking",
    description: "Customers monitor order progress in real time.",
    color: "from-primary to-orange-500"
  },
  {
    icon: ShoppingCart,
    title: "Upselling & Optimization",
    description: "Smart add-ons and upgrades increase ticket size.",
    color: "from-orange-600 to-primary"
  },
  {
    icon: TrendingUp,
    title: "Menu Engineering",
    description: "Identify top performers and optimize menu profitability.",
    color: "from-primary to-orange-600"
  },
  {
    icon: BarChart3,
    title: "Power Analytics",
    description: "Insights into sales, staff efficiency, and menu performance.",
    color: "from-orange-500 to-primary"
  },
  {
    icon: LayoutDashboard,
    title: "Real-Time Management",
    description: "Track sales, edit menus, and manage operations live.",
    color: "from-primary to-orange-400"
  },
  {
    icon: Languages,
    title: "Multi-Language Support",
    description: "Serve global customers effortlessly across languages.",
    color: "from-orange-600 to-primary"
  },
  {
    icon: Brain,
    title: "Smart Knowledge System",
    description: "AI improves continuously from chef and staff input.",
    color: "from-primary to-orange-500"
  },
  {
    icon: CreditCard,
    title: "Built-In Global Payments",
    description: "Card, Apple Pay, Google Pay, and multi-currency support.",
    color: "from-orange-400 to-primary"
  },
  {
    icon: Plug,
    title: "POS & Kitchen Integration",
    description: "Orders flow directly into existing POS and kitchen systems.",
    color: "from-primary to-orange-600"
  },
  {
    icon: BellRing,
    title: "Staff Alerts & Escalation",
    description: "Notify staff instantly when human assistance is needed.",
    color: "from-orange-500 to-primary"
  }
];
// --- End updated featureCards ---

// Pricing plans for the new Pricing component
const pricingPlans = [
  {
    name: "Starter",
    price: "149",
    yearlyPrice: "119",
    period: "month",
    bestFor: "Small cafés, food trucks, and new restaurants",
    additionalCosts: "+ AI usage costs",
    features: [
      "AI Waiter",
      "Multi-language support",
      "Digital menu (QR-based)",
      "Restaurant dashboard",
      "Menu & price management",
      "Basic AI Business Analyst",
      "Basic analytics"
    ],
    description: "Simple setup for restaurants getting started",
    badge: "30-day risk free trial",
    color: "black",
    buttonText: "Get Started",
    href: "#",
    isPopular: false
  },
  {
    name: "Growth",
    price: "299",
    yearlyPrice: "249",
    period: "month",
    bestFor: "Busy casual restaurants",
    additionalCosts: "+ AI usage costs",
    features: [
      "Everything in Starter, plus:",
      "AI Receptionist",
      "AI-device-enabled digital menu",
      "Reservations management",
      "Online phone ordering",
      "Advanced analytics"
    ],
    description: "Scale your operations with advanced AI features",
    color: "black",
    buttonText: "Get Started",
    href: "#",
    isPopular: true
  },
  {
    name: "Pro",
    price: "499",
    yearlyPrice: "399",
    period: "month",
    bestFor: "High-volume or multi-shift restaurants",
    additionalCosts: "+ AI usage costs",
    features: [
      "Everything in Growth, plus:",
      "POS integration",
      "Advanced AI Business Analyst",
      "Kitchen Display System (KDS) enabled",
      "Mobile ordering enabled",
      "Priority support"
    ],
    description: "Full suite for enterprise-level volume",
    badge: "Free POS Device",
    color: "black",
    buttonText: "Get Started",
    href: "#",
    isPopular: false
  }
];

// --- Landing Page Integration Data ---
const outcomeCards = [
  {
    icon: MessageCircleQuestion,
    title: "Fewer staff interruptions",
    description: "Our AI waiter explains the menu, answers guest questions, handles modifications, takes orders, and processes payments. That means fewer call-outs to servers, fewer distractions, and more time spent delivering great service and food.",
  },
  {
    icon: Clock,
    title: "Faster ordering and higher turnover",
    description: "Guests order when they're ready, no waiting to flag down a server. Orders flow directly to the kitchen as clean, accurate tickets, reducing back-and-forth and keeping service moving during peak hours.",
  },
  {
    icon: Sparkles,
    title: "Built-in upsells that feel natural",
    description: "The AI waiter & AI receptionist are context-aware, they provide recommendations for drinks, sides, and upgrades increase average check size without pressure or awkward sales tactics. Guests feel guided, not sold to.",
  },
  {
    icon: Phone,
    title: "Never miss a call or reservation",
    description: "Waiterix answers calls 24/7, shares hours, location, and menu information, captures reservations, and routes special requests, so opportunities aren't lost when staff are busy on the floor.",
  },
  {
    icon: BarChart3,
    title: "Clear insights, better decisions",
    description: "Beyond service, the AI Business Analyst turns operational data into actionable insights highlighting top-selling items, low performers, staffing signals, and shift level trends you can act on immediately.",
  },
  {
    icon: TrendingUp,
    title: "Relief that scales with demand",
    description: "Whether it's a slow afternoon or a packed Friday night, Waiterix scales instantly. No schedules. No call-outs. No added payroll.",
  },
];

const teamMembers = [
  {
    name: "AI Waiter",
    description: "Explains the menu, answers questions, recommends items, takes orders, and helps increase check size with natural upsells.",
    image: "/assets/ai-waiter.jpeg",
    tags: ["QR + device", "Multi-language", "Upsells"],
  },
  {
    name: "AI Receptionist",
    description: "Answers calls 24/7, provides hours/location/menu info, captures reservations, and routes special requests, reducing missed calls.",
    image: "/assets/ai-receptionist.jpeg",
    tags: ["Calls", "Reservations", "Routing"],
  },
  {
    name: "AI Business Specialist",
    description: "Turns operational data into clear actions, top selling items, low performers, staffing signals, and shift insights you can use immediately.",
    image: "/assets/ai-business-specialist.jpeg",
    tags: ["Dashboards", "Insights", "Performance"],
  },
];

const howItWorksSteps = [
  {
    num: 1,
    title: "Place AI devices or QR codes on tables",
    description: "Guests use our AI device or scan a QR code to open your menu instantly (mobile-optimized). No app required",
  },
  {
    num: 2,
    title: "Guests order by tapping or talking",
    description: "Waiterix answers questions, makes recommendations, and takes orders.",
  },
  {
    num: 3,
    title: "Orders flow to the kitchen in real time",
    description: "Clean tickets, live status updates, fewer mistakes and remakes.",
  },
  {
    num: 4,
    title: "Payments",
    description: "Customers can pay online or later at the register and track their orders in real time.",
  },
];

const products = [
  {
    id: "pos",
    name: "POS Terminal",
    description: "Counter or cashier station for order entry and checkout. Built for speed, reliability, and busy shifts.",
    image: "/assets/pos.jpeg",
    price: "$399",
    features: [
      "Fast checkout and receipts",
      "Supports common payment flows",
      "Designed for high volume operations",
    ],
    chips: ["Checkout", "Receipts", "High volume"],
    useCases: "Best for checkout at the counter, cashier workflows, and locations that need a reliable primary payment station.",
  },
  {
    id: "kds",
    name: "KDS",
    description: "Kitchen Display System that organizes tickets, reduces missed mods, and improves throughput.",
    image: "/assets/kds.png",
    price: "$199",
    features: [
      "Clear ticket columns and stages",
      "Designed for fewer errors under pressure",
      "Improves handoff and pacing",
    ],
    chips: ["Ticket stages", "Throughput", "Less rework"],
    useCases: "Best for kitchens that need clearer ticket flow, faster handoffs, and fewer errors during rush.",
  },
  {
    id: "mobile-ordering",
    name: "Mobile Ordering Station",
    description: "An iPad style station for counter pickup, self-serve ordering, or staff assisted ordering anywhere.",
    image: "/assets/mobile-ordering-station.jpeg",
    price: "$99",
    features: [
      "Ideal for quick service and events",
      "Compact footprint, easy to place",
      "Great for line busting",
    ],
    chips: ["Self-serve", "Line busting", "Events"],
    useCases: "Best for fast casual, popups, and line-busting. Place it at the counter, near pickup, or with staff for fast order capture.",
  },
  {
    id: "ai-device",
    name: "AI Device",
    description: "A guest-facing device for table-side experiences, smart ordering, and guided menu discovery.",
    image: "/assets/ai-device.jpeg",
    price: "$149",
    features: [
      "Supports conversational ordering flows",
      "Helps guests navigate the menu",
      "Reduces staff interruptions",
    ],
    chips: ["Table-side", "Guided ordering", "Upsells"],
    useCases: "Best for table-side experiences and guided ordering. Helps guests discover items, confirm choices, and move to checkout with less delay.",
  },
];

export default function Home() {
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Demo booking dialog state
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '' });
  const [dialogMode, setDialogMode] = useState<'demo' | 'other'>('demo');

  useEffect(() => {
    if (user && !isLoading) {
      console.log("User authenticated, redirecting to dashboard");
      setLocation('/dashboard');
    }
  }, [user, isLoading, setLocation]);

  // --- Auth handlers remain unchanged ---
  const handleGoogleSignIn = async () => {
    try {
      setIsAuthenticating(true);
      await signInWithGoogle();
      setShowAuthDialog(false);
    } catch (error: any) {
      toast({
        title: "Sign-in failed",
        description: error.message || "Failed to sign in with Google",
        variant: "destructive",
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please enter email and password",
        variant: "destructive",
      });
      return;
    }

    if (authMode === 'signup') {
      const hasNumber = /\d/.test(password);
      const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      const hasLetter = /[a-zA-Z]/.test(password);

      if (password.length < 6 || !hasNumber || !hasSymbol || !hasLetter) {
        toast({
          title: "Weak password",
          description: "Password must be at least 6 characters, including a letter, a number, and a symbol",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setIsAuthenticating(true);

      if (authMode === 'signup') {
        await signUpWithEmail(email, password, displayName || undefined);
        toast({
          title: "Account created",
          description: "Welcome to Waiterix! Redirecting to dashboard...",
        });
      } else {
        await signInWithEmail(email, password);
        toast({
          title: "Signed in",
          description: "Welcome back! Redirecting to dashboard...",
        });
      }

      setShowAuthDialog(false);
      setEmail('');
      setPassword('');
      setDisplayName('');
    } catch (error: any) {
      let errorMessage = "Authentication failed";

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = "Email already in use. Try signing in instead.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Password is too weak";
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = "Invalid email or password";
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = "Invalid email or password";
      }

      toast({
        title: "Authentication failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAuthenticating(true);

      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setForgotPasswordSent(true);
        toast({
          title: "Check your email",
          description: "If an account exists, we've sent a password reset link.",
        });
      }
    } catch (error: any) {
      // Still show success to prevent email enumeration
      setForgotPasswordSent(true);
      toast({
        title: "Check your email",
        description: "If an account exists, we've sent a password reset link.",
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  // HubSpot form submission for demo bookings
  const submitToHubSpot = async (data: { name: string; email: string; phone: string }, source: string) => {
    const portalId = "244843893";
    const formId = "b808aa91-1754-4c46-b1dc-c68e7f0912bb";
    const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    const fields = [
      { name: "firstname", value: data.name },
      { name: "email", value: data.email },
      { name: "phone", value: data.phone }
    ];

    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields,
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            hutk: document.cookie.match(/hubspotutk=([^;]+)/)?.[1]
          }
        })
      });
      console.log(`[HubSpot] Home lead submitted from ${source}`);
    } catch (err) {
      console.error("[HubSpot] Submission error:", err);
    }
  };

  const handleDemoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Store lead data locally
    localStorage.setItem("waiterixDemoLead", JSON.stringify({ ...demoForm, ts: Date.now(), source: 'home_demo' }));
    // Send to HubSpot
    submitToHubSpot(demoForm, 'home_demo');
    // Close dialog and open Calendly
    setShowDemoDialog(false);
    window.open('https://calendly.com/kabir-waiterix/30min', '_blank');
    // Reset form
    setDemoForm({ name: '', email: '', phone: '' });
  };

  const openDemoDialog = () => {
    setShowDemoDialog(true);
  };

  // --- Loading and redirect remain ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="loader-auth-home" />
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b glass backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="Waiterix" className="h-12 hover-lift smooth-transition" />
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <Button
              variant="ghost"
              onClick={() => { setAuthMode('signin'); setShowAuthDialog(true); }}
              data-testid="button-sign-in"
            >
              Sign In
            </Button>
            <Button
              onClick={() => { setAuthMode('signup'); setShowAuthDialog(true); }}
              data-testid="button-get-started"
              className="btn-glow shadow-glow hover-lift"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with Video Background */}
      <section className="py-20 md:py-32 relative overflow-hidden min-h-[80vh] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover z-0"
          onCanPlay={(e) => (e.target as HTMLVideoElement).play()}
        >
          <source src="/hero-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-black/50 z-[1]"></div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-orange-400/20 z-[2]"></div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="bounce-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">
              AI-powered systems built for{" "}
              <span className="text-orange-400">modern hospitality businesses</span>
            </h1>
          </div>
          <p className="text-xl text-white/80 mb-8 max-w-4xl mx-auto text-center md:whitespace-nowrap">
            Reduce operations burden, increase efficiency and improve customer satisfaction
          </p>

          {/* Book a Demo Link */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <Button
              size="lg"
              className="shadow-glow hover-lift text-lg px-8 py-6 bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="button-hero-demo"
              onClick={openDemoDialog}
            >
              Book a Demo
            </Button>


          </div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-orange-400/10 rounded-full blur-xl float-delayed"></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-orange-500/10 rounded-full blur-xl float"></div>
      </section>
      <div className="flex justify-center mt-12 mb-2">
        <Button
          size="lg"
          onClick={() => { setAuthMode('signup'); setShowAuthDialog(true); }}
          data-testid="button-mid-get-started"
          className="
          relative
          text-lg font-bold uppercase tracking-wider
          px-14 py-6
          rounded-full
          bg-primary text-primary-foreground
          shadow-[0_0_40px_hsl(var(--primary)/0.6)]
          animate-pulse [animation-duration:3.5s]
          hover:animate-none hover:scale-110
          transition-all duration-300
"

        >
          Get Started
        </Button>
      </div>

      <div className="flex flex-col items-center text-center gap-2 text-sm text-black mb-12">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-orange-400" />
          <span className="text-black">
            30-day guarantee • No setup fees • Cancel anytime
          </span>
        </div>
        <p className="text-sm text-black">
          Plus, Complimentary QR-Code Display Tags
        </p>
      </div>

      {/* --- NEW Integrated Landing Content --- */}

      {/* Outcomes Section (Relief, not software) */}
      <section className="py-24 bg-gradient-to-b from-white to-muted/10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              We sell relief, <span className="text-orange-400">not software.</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Our clients don't buy AI. They buy smoother shifts, fewer mistakes, and more revenue,
              without hiring more people or overhauling their operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {outcomeCards.map((card, index) => (
              <Card key={index} className="p-8 border-2 border-primary/10 bg-white shadow-[12px_12px_0_0_rgba(249,115,22,0.03)] hover:shadow-[16px_16px_0_0_rgba(249,115,22,0.06)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-primary/5 border-2 border-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:border-primary transition-all duration-300">
                    <card.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{card.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Your Team Section (AI Roles) - LIGHT ORANGE THEME */}
      <section className="py-24 bg-orange-50/50 border-y-2 border-orange-100 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.1),transparent_70%)]"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black tracking-tight">Meet your future team</h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Three AI roles that cover guest ordering, inbound calls, and business insights, so your staff can focus on hospitality.
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                size="lg"
                variant="outline"
                className="border-orange-200 hover:bg-orange-100 bg-white/50 shadow-sm"
                onClick={openDemoDialog}
              >
                Book a demo
              </Button>
              <Button
                size="lg"
                className="btn-glow shadow-glow"
                onClick={() => window.open('/menu/83b5e11f-46cf-4572-a383-d5806f73966c?table=2&showLeadForm=true', '_blank')}
              >
                Try the AI waiter
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {teamMembers.map((member, index) => (
              <Card key={index} className="bg-white border-2 border-orange-100 p-6 overflow-hidden group hover:border-primary/50 shadow-[14px_14px_0_0_rgba(249,115,22,0.05)] hover:shadow-[18px_18px_0_0_rgba(249,115,22,0.08)] hover:-translate-y-1 transition-all duration-300">
                <div className="aspect-[4/5] rounded-xl overflow-hidden border border-orange-50 mb-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-950/20 via-transparent to-transparent opacity-40"></div>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-110"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://placehold.co/400x500/fff7ed/ea580c?text=${member.name}`;
                    }}
                  />
                  <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                    {member.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg bg-white border-2 border-primary/20 text-primary shadow-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-3 text-black">{member.name}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">{member.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How Waiterix Works Section (Process) */}
      <section className="py-24 bg-slate-50/80 border-y-2 border-slate-100 relative">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-2xl mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">How Waiterix works</h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Use it alongside staff today, or let it handle peak-hour ordering automatically.
              No app downloads for guests.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step) => (
              <div key={step.num} className="group flex flex-col p-8 rounded-2xl bg-white border-2 border-muted/20 shadow-[8px_8px_0_0_rgba(0,0,0,0.03)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-orange-400 flex items-center justify-center mb-8 shadow-lg group-hover:rotate-6 transition-transform">
                  <span className="text-2xl font-black text-white">{step.num}</span>
                </div>
                <h4 className="text-xl font-bold mb-4 tracking-tight">{step.title}</h4>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Why Waiterix Section (original) --- */}
      <section className="py-20 bg-white relative border-t">

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Waiterix?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">

            {/* Labor Efficiency */}
            <Card className="p-8 border-2 border-muted/10 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <AnimatedGauge
                  value={40}
                  label=""
                  hideValue
                  color="hsl(var(--primary))"
                />
                <h3 className="font-bold text-xl mb-3 mt-4 text-center">
                  Reduced Labor Burden
                </h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  Waiterix automates core waiter tasks, helping restaurants cut nearly half of their front-of-house labor burden.
                </p>
              </div>
            </Card>

            {/* Accuracy */}
            <Card className="p-8 border-2 border-muted/10 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <AnimatedGauge
                  value={100}
                  label=""
                  hideValue
                  color="hsl(var(--chart-2))"
                />
                <h3 className="font-bold text-xl mb-3 mt-4 text-center">
                  Improved Accuracy
                </h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  Our end-to-end AI ordering system eliminates order errors and reduces bottlenecks for smoother operations.
                </p>
              </div>
            </Card>

            {/* Revenue */}
            <Card className="p-8 border-2 border-muted/10 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <AnimatedGauge
                  value={25}
                  label=""
                  hideValue
                  color="hsl(var(--chart-3))"
                />
                <h3 className="font-bold text-xl mb-3 mt-4 text-center">
                  Higher Revenue Potential
                </h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  The AI waiter excels at upselling and cross-selling, boosting average check sizes and overall revenue.
                </p>
              </div>
            </Card>

            {/* Satisfaction */}
            <Card className="p-8 border-2 border-muted/10 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden">
              <div className="relative z-10 flex flex-col items-center">
                <AnimatedGauge
                  value={5}
                  maxValue={5}
                  label=""
                  hideValue
                  color="hsl(var(--chart-4))"
                />
                <h3 className="font-bold text-xl mb-3 mt-4 text-center">
                  Better Guest Experience
                </h3>
                <p className="text-muted-foreground text-center leading-relaxed">
                  Fast, accurate, and personalized service ensures customers enjoy a smoother and more satisfying dining experience.
                </p>
              </div>
            </Card>

          </div>
        </div>
      </section>


      {/* Features Section - Auto-scrolling Carousel */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Features</h2>
            <p className="text-muted-foreground">Powerful features to transform your restaurant</p>
          </div>
        </div>

        <MarqueeCarousel speed={40} pauseOnHover={true} direction="left">
          {featureCards.map((f, i) => (
            <Card key={i} className="p-8 border-2 border-muted/10 bg-white shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden w-[300px] min-h-[280px]">
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <f.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3 text-foreground group-hover:text-primary transition-colors">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            </Card>
          ))}
        </MarqueeCarousel>
      </section>

      {/* --- Integrated Products Section --- */}

      {/* Products and pricing Section */}
      <section className="py-24 bg-orange-50/50 border-y-2 border-orange-100 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.1),transparent_70%)]"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">What each device is best for</h2>
            <p className="text-xl text-muted-foreground max-w-3xl leading-relaxed">
              Use these as building blocks. Most operators start with one station and expand into kitchen display and guest-facing devices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="p-8 flex flex-col sm:flex-row gap-8 items-start bg-white border-2 border-muted/20 shadow-[8px_8px_0_0_rgba(0,0,0,0.02)] hover:shadow-[12px_12px_0_0_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300">
                <div className="w-full sm:w-48 h-36 bg-muted/30 rounded-2xl flex-shrink-0 overflow-hidden border-2 border-muted/10 p-4">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://placehold.co/400x300/f8fafc/64748b?text=${product.name}`;
                    }}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-2xl mb-3 tracking-tight">{product.name}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{product.useCases}</p>
                  <div className="flex flex-wrap gap-3">
                    {product.chips.map((chip, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-1.5 rounded-lg border-2 border-primary/10 bg-primary/5 text-primary text-xs font-bold uppercase tracking-wider shadow-sm"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Advertisement / Book Demo Section */}
      <section className="py-20 md:py-32 relative overflow-hidden min-h-[80vh] flex items-center bg-slate-950">
        {/* Decorative background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,40,200,0.1),transparent_70%)] z-[1]"></div>

        {/* Gradient Overlay - different style from hero */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/40 via-transparent to-orange-600/20 z-[2]"></div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 relative z-10 w-full">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white drop-shadow-lg">
              Experience Waiterix Now
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Try our AI waiter demo and see the future of restaurant ordering
            </p>
            <Link href="/menu/83b5e11f-46cf-4572-a383-d5806f73966c?table=2&showLeadForm=true">
              <Button
                size="lg"
                className="bg-white text-black hover:bg-white/90 font-semibold px-8 py-6 text-lg shadow-xl hover-lift"
              >
                Try Waiterix
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <Pricing
        plans={pricingPlans}
        onButtonClick={() => { setAuthMode('signup'); setShowAuthDialog(true); }}
        title="Simple, Transparent Pricing"
        description="Full refund if you're not satisfied within 30 days"
      /> */}

      {/* Contact Us Section */}
      <div className="flex justify-center py-16 bg-muted/20">
        <Button
          size="lg"
          onClick={() => window.open('mailto:support@waiterix.com?subject=Contact Inquiry', '_blank')}
          data-testid="button-contact-us"
          className="btn-glow shadow-glow hover-lift text-lg px-10 py-6"
        >
          Contact Us
        </Button>
      </div>

      {/* Footer */}
      <footer className="border-t py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">

            {/* Powered By */}
            <div className="mb-8">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                Powered by
              </p>

              <div className="flex justify-center items-center gap-8 opacity-80">
                <img
                  src="/assets/openai.png"
                  alt="Powered by OpenAI"
                  className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                />
                <img
                  src="/assets/stripe.png"
                  alt="Powered by Stripe"
                  className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                />
                <img
                  src="/assets/twilio.png"
                  alt="Powered by Twilio"
                  className="h-6 grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            </div>

            {/* Footer Links */}
            <div className="flex justify-center gap-6 mb-4 text-sm">
              <Link
                href="/terms"
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-terms"
              >
                Terms & Conditions
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link
                href="/privacy"
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="link-privacy"
              >
                Privacy Policy
              </Link>
            </div>

            <p className="text-sm text-muted-foreground">
              © 2025 Waiterix. All rights reserved.
            </p>

            {/* Disclaimer */}
            <p className="text-xs text-muted-foreground/70 mt-6 max-w-6xl mx-auto leading-relaxed">
              All information, services, products, and content on this website are provided for general informational purposes only and should not be considered professional advice. No rights or guarantees can be derived from the content on this site, and use of the website is governed by our Terms of Use. Reported results are based on either extrapolation or feedback from individual clients and may vary from one restaurant to another.
            </p>

            <p className="text-sm font-medium text-muted-foreground mt-2">
              A product of Harmonia Enterprises LLC
            </p>
          </div>
        </div>
      </footer>


      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={(open) => {
        setShowAuthDialog(open);
        if (!open) {
          // Reset state when dialog closes
          setForgotPasswordSent(false);
          setAuthMode('signin');
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-auth">
          <DialogHeader>
            <DialogTitle>
              {authMode === 'forgot'
                ? 'Reset Password'
                : authMode === 'signin'
                  ? 'Sign In'
                  : 'Create Account'}
            </DialogTitle>
            <DialogDescription>
              {authMode === 'forgot'
                ? "Enter your email and we'll send you a link to reset your password."
                : authMode === 'signin'
                  ? 'Welcome back! Sign in to access your restaurant dashboard.'
                  : 'Join Waiterix to start serving customers with AI-powered voice ordering.'}
            </DialogDescription>
          </DialogHeader>

          {/* Forgot Password Mode */}
          {authMode === 'forgot' ? (
            <div className="space-y-4">
              {forgotPasswordSent ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Check your email</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    If an account exists with {email}, we've sent a password reset link.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAuthMode('signin');
                      setForgotPasswordSent(false);
                      setEmail('');
                    }}
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isAuthenticating}
                      required
                      data-testid="input-reset-email"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isAuthenticating}
                    data-testid="button-send-reset"
                  >
                    {isAuthenticating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>

                  <div className="text-center text-sm">
                    <button
                      type="button"
                      onClick={() => setAuthMode('signin')}
                      className="text-primary hover:underline"
                      disabled={isAuthenticating}
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            /* Sign In / Sign Up Mode */
            <div className="space-y-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={isAuthenticating}
                data-testid="button-google-auth"
              >
                {isAuthenticating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                Continue with Google
              </Button>

              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                  or
                </span>
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-4">
                {authMode === 'signup' && (
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Restaurant Name (optional)</Label>
                    <Input
                      id="displayName"
                      placeholder="Your Restaurant"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      disabled={isAuthenticating}
                      data-testid="input-display-name"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isAuthenticating}
                    required
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {authMode === 'signin' && (
                      <button
                        type="button"
                        onClick={() => setAuthMode('forgot')}
                        className="text-xs text-primary hover:underline"
                        disabled={isAuthenticating}
                        data-testid="button-forgot-password"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isAuthenticating}
                    required
                    data-testid="input-password"
                  />
                  {authMode === 'signup' && (
                    <p className="text-xs text-muted-foreground">At least 6 characters, including a number and a symbol</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isAuthenticating}
                  data-testid="button-email-auth"
                >
                  {isAuthenticating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {authMode === 'signin' ? 'Signing in...' : 'Creating account...'}
                    </>
                  ) : (
                    authMode === 'signin' ? 'Sign In' : 'Create Account'
                  )}
                </Button>
              </form>

              <div className="text-center text-sm">
                {authMode === 'signin' ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setAuthMode('signup')}
                      className="text-primary hover:underline"
                      disabled={isAuthenticating}
                      data-testid="button-switch-signup"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setAuthMode('signin')}
                      className="text-primary hover:underline"
                      disabled={isAuthenticating}
                      data-testid="button-switch-signin"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Demo Booking Dialog */}
      <Dialog open={showDemoDialog} onOpenChange={() => { }}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Book a Demo</DialogTitle>
            <DialogDescription>
              Fill in your details and we'll redirect you to schedule your demo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDemoSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="demo-name">Name</Label>
              <Input
                id="demo-name"
                placeholder="Your name"
                value={demoForm.name}
                onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="demo-email">Email</Label>
              <Input
                id="demo-email"
                type="email"
                placeholder="you@example.com"
                value={demoForm.email}
                onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="demo-phone">Phone Number</Label>
              <Input
                id="demo-phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={demoForm.phone}
                onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                required
              />
            </div>

            <Button type="submit" className="w-full btn-glow shadow-glow" data-testid="button-submit-demo">
              {dialogMode === 'demo' ? 'Continue to Schedule' : 'Submit'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div >
  );
}
